import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  X,
  Send,
  Package,
  Palette,
  Image as ImageIcon,
  Truck,
  HelpCircle,
  Mail,
  Phone,
  Languages,
} from "lucide-react";
import { API_BASE_URL } from "../../config";
import { Lang, translations, matchIntent } from "./translations";
import { BotFaceIcon } from "./BotFaceIcon";
import styles from "./ChatWidget.module.css";

const WHATSAPP_LINK = "https://wa.me/201299939647";
const WHATSAPP_DISPLAY = "01299939647";
const CONTACT_EMAIL = "iciledawastudio@gmail.com";

type ChatMessage =
  | { id: string; kind: "bot-text"; text: string }
  | { id: string; kind: "user-text"; text: string }
  | { id: string; kind: "bot-typing" }
  | {
      id: string;
      kind: "buttons";
      options: { label: string; icon?: React.ReactNode; onClick: () => void }[];
    }
  | {
      id: string;
      kind: "select";
      placeholder: string;
      options: string[];
      onSelect: (value: string) => void;
    };

type AwaitingInput = "reference-number" | null;

// Plain Omit collapses a discriminated union to its common keys only —
// distribute it over each member so addMessage() still accepts any variant.
type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

let idCounter = 0;
const nextId = () => `msg-${Date.now()}-${idCounter++}`;

export function ChatWidget() {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [awaiting, setAwaiting] = useState<AwaitingInput>(null);
  const [lang, setLang] = useState<Lang>("en");
  const [hintDismissed, setHintDismissed] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Always read the latest translations/lang inside closures via a ref,
  // since flow functions are defined once but called later from callbacks.
  const langRef = useRef(lang);
  langRef.current = lang;
  const t = translations[lang];

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isOpen && !hasOpenedOnce) {
      setHasOpenedOnce(true);
      showGreeting();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const addMessage = (message: DistributiveOmit<ChatMessage, "id">) => {
    setMessages((prev) => [...prev, { ...message, id: nextId() } as ChatMessage]);
  };

  const addBotText = (text: string) => addMessage({ kind: "bot-text", text });
  const addUserText = (text: string) => addMessage({ kind: "user-text", text });

  const showTyping = () => {
    const typingId = nextId();
    setMessages((prev) => [...prev, { id: typingId, kind: "bot-typing" }]);
    return () => {
      setMessages((prev) => prev.filter((m) => m.id !== typingId));
    };
  };

  const showMainMenu = () => {
    const tr = translations[langRef.current];
    addMessage({
      kind: "buttons",
      options: [
        { label: tr.menuTrackOrder, icon: <Package size={15} />, onClick: startTrackOrder },
        { label: tr.menuKits, icon: <ImageIcon size={15} />, onClick: showKits },
        { label: tr.menuPalettes, icon: <Palette size={15} />, onClick: showPalettes },
        { label: tr.menuWorkshops, icon: <ImageIcon size={15} />, onClick: showWorkshops },
        { label: tr.menuShipping, icon: <Truck size={15} />, onClick: showShipping },
        { label: tr.menuSomethingElse, icon: <HelpCircle size={15} />, onClick: showFallback },
      ],
    });
  };

  const showGreeting = () => {
    addBotText(translations[langRef.current].greeting);
    showMainMenu();
  };

  const backToMenu = () => {
    addBotText(translations[langRef.current].backToMenu);
    showMainMenu();
  };

  // ---- Track Order ----
  const startTrackOrder = () => {
    const tr = translations[langRef.current];
    addUserText(tr.menuTrackOrder);
    setAwaiting("reference-number");
    addBotText(tr.trackOrderPrompt);
  };

  const lookupOrder = async (referenceNumber: string) => {
    const tr = translations[langRef.current];
    const stopTyping = showTyping();
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/order-lookup/${encodeURIComponent(referenceNumber)}`,
      );
      const data = await response.json();
      stopTyping();

      if (!data.found) {
        addBotText(tr.orderNotFound(referenceNumber));
        showContact();
        backToMenu();
        return;
      }

      const statusLabel =
        data.status === "confirmed"
          ? tr.statusConfirmed
          : data.status === "cancelled"
            ? tr.statusCancelled
            : data.status === "mixed"
              ? tr.statusMixed
              : tr.statusPending;
      const paidLabel = data.isPaid ? tr.paidLabel : tr.notPaidLabel;
      const date = new Date(data.createdAt).toLocaleDateString(tr.locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const itemsList = (data.items || [])
        .map(
          (item: { productName: string; quantity?: number }) =>
            `• ${item.productName}${item.quantity ? ` (${tr.qtyLabel} ${item.quantity})` : ""}`,
        )
        .join("\n");

      addBotText(
        `${tr.orderFoundIntro(data.referenceNumber)}\n\n` +
          `${itemsList}\n` +
          `${tr.statusLabelPrefix}: ${statusLabel}\n` +
          `${tr.paymentLabelPrefix}: ${paidLabel}\n` +
          `${tr.totalLabel}: EGP ${data.total}\n` +
          `${tr.placedOnLabel}: ${date}`,
      );
      backToMenu();
    } catch (error) {
      stopTyping();
      addBotText(tr.lookupError);
      backToMenu();
    }
  };

  // ---- Paint Kits ----
  const showKits = async () => {
    const tr = translations[langRef.current];
    addUserText(tr.menuKits);
    const stopTyping = showTyping();
    try {
      const response = await fetch(`${API_BASE_URL}/api/painting-kits`);
      const data = await response.json();
      stopTyping();
      const kits = (data.kits || []).filter((k: any) => k.inStock).slice(0, 5);

      if (kits.length === 0) {
        addBotText(tr.kitsEmpty);
      } else {
        addBotText(
          `${tr.kitsIntro}\n\n` + kits.map((k: any) => `• ${k.name} — EGP ${k.price}`).join("\n"),
        );
      }
      addMessage({
        kind: "buttons",
        options: [{ label: tr.viewAllKits, onClick: () => goTo("/products/kits") }],
      });
      backToMenu();
    } catch (error) {
      stopTyping();
      addBotText(tr.kitsError);
      backToMenu();
    }
  };

  // ---- Color Palettes ----
  const showPalettes = async () => {
    const tr = translations[langRef.current];
    addUserText(tr.menuPalettes);
    const stopTyping = showTyping();
    try {
      const response = await fetch(`${API_BASE_URL}/api/color-palettes`);
      const data = await response.json();
      stopTyping();
      const palettes = (data.palettes || []).filter((p: any) => p.inStock).slice(0, 5);

      if (palettes.length === 0) {
        addBotText(tr.palettesEmpty);
      } else {
        addBotText(
          `${tr.palettesIntro}\n\n` +
            palettes.map((p: any) => `• ${p.name} — EGP ${p.price}`).join("\n"),
        );
      }
      addMessage({
        kind: "buttons",
        options: [{ label: tr.viewAllPalettes, onClick: () => goTo("/products/palettes") }],
      });
      backToMenu();
    } catch (error) {
      stopTyping();
      addBotText(tr.palettesError);
      backToMenu();
    }
  };

  // ---- Workshops ----
  const showWorkshops = async () => {
    const tr = translations[langRef.current];
    addUserText(tr.menuWorkshops);
    const stopTyping = showTyping();
    try {
      const response = await fetch(`${API_BASE_URL}/api/workshops/`);
      const data = await response.json();
      stopTyping();
      const workshops = (data.workshops || []).slice(0, 5);

      if (workshops.length === 0) {
        addBotText(tr.workshopsEmpty);
      } else {
        addBotText(
          `${tr.workshopsIntro}\n\n` +
            workshops
              .map((w: any) => {
                const date = new Date(w.date).toLocaleDateString(tr.locale, {
                  month: "short",
                  day: "numeric",
                });
                const seats =
                  w.availableSeats > 0 ? tr.spotsLeft(w.availableSeats) : tr.fullyBooked;
                return `• ${w.name} — ${date} (${seats})`;
              })
              .join("\n"),
        );
      }
      addMessage({
        kind: "buttons",
        options: [{ label: tr.viewAllWorkshops, onClick: () => goTo("/products/workshops") }],
      });
      backToMenu();
    } catch (error) {
      stopTyping();
      addBotText(tr.workshopsError);
      backToMenu();
    }
  };

  // ---- Shipping ----
  const showShipping = async () => {
    const tr = translations[langRef.current];
    addUserText(tr.menuShipping);
    const stopTyping = showTyping();
    try {
      const response = await fetch(`${API_BASE_URL}/api/governorates`);
      const data = await response.json();
      stopTyping();
      const governorates = data.governorates || [];

      addBotText(tr.shippingPrompt);
      addMessage({
        kind: "select",
        placeholder: tr.shippingSelectPlaceholder,
        options: governorates.map((g: any) => g.name),
        onSelect: (name) => {
          const trNow = translations[langRef.current];
          const match = governorates.find((g: any) => g.name === name);
          addUserText(name);
          if (match) {
            addBotText(trNow.shippingResult(name, match.shippingCost));
          } else {
            addBotText(trNow.shippingNotFound);
          }
          backToMenu();
        },
      });
    } catch (error) {
      stopTyping();
      addBotText(tr.shippingError);
      backToMenu();
    }
  };

  // ---- Contact / Fallback ----
  const showContact = () => {
    addMessage({
      kind: "buttons",
      options: [
        {
          label: WHATSAPP_DISPLAY,
          icon: <Phone size={15} />,
          onClick: () => window.open(WHATSAPP_LINK, "_blank", "noopener,noreferrer"),
        },
        {
          label: CONTACT_EMAIL,
          icon: <Mail size={15} />,
          onClick: () => window.open(`mailto:${CONTACT_EMAIL}`, "_blank"),
        },
      ],
    });
  };

  const showFallback = () => {
    const tr = translations[langRef.current];
    addUserText(tr.menuSomethingElse);
    addBotText(tr.fallbackIntro);
    showContact();
  };

  const handleSend = () => {
    const tr = translations[langRef.current];
    const text = inputValue.trim();
    if (!text) return;
    setInputValue("");

    if (awaiting === "reference-number") {
      addUserText(text);
      setAwaiting(null);
      lookupOrder(text.toUpperCase());
      return;
    }

    addUserText(text);
    const intent = matchIntent(text);
    switch (intent) {
      case "track":
        startTrackOrder();
        break;
      case "kits":
        showKits();
        break;
      case "palettes":
        showPalettes();
        break;
      case "workshops":
        showWorkshops();
        break;
      case "shipping":
        showShipping();
        break;
      case "contact":
        addBotText(tr.contactIntro);
        showContact();
        break;
      default:
        addBotText(tr.fallbackUnknown);
        showContact();
        backToMenu();
    }
  };

  const toggleLang = () => {
    setLang((prev) => (prev === "en" ? "ar" : "en"));
  };

  // Close the chat before leaving the page, so it doesn't stay open (and,
  // full-screen on mobile, hidden) behind the page the user just navigated to.
  const goTo = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  return (
    <div className={styles.widgetRoot}>
      {isOpen && (
        <div className={styles.panel} dir={t.dir}>
          <div className={styles.header}>
            <div className={styles.headerInfo}>
              <div className={styles.headerAvatar}>
                <BotFaceIcon size={24} />
              </div>
              <div>
                <p className={styles.headerTitle}>{t.headerTitle}</p>
                <p className={styles.headerSubtitle}>{t.headerSubtitle}</p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <button
                className={styles.langToggleBtn}
                onClick={toggleLang}
                aria-label="Switch language"
              >
                <Languages size={14} />
                {t.langToggleLabel}
              </button>
              <button
                className={styles.closeBtn}
                onClick={() => setIsOpen(false)}
                aria-label={t.closeChat}
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className={styles.messages} ref={scrollRef}>
            {messages.map((message) => {
              if (message.kind === "bot-text") {
                return (
                  <div key={message.id} className={styles.botRow}>
                    <div className={styles.botBubble}>
                      {message.text.split("\n").map((line, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <br />}
                          {line}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                );
              }
              if (message.kind === "user-text") {
                return (
                  <div key={message.id} className={styles.userRow}>
                    <div className={styles.userBubble}>{message.text}</div>
                  </div>
                );
              }
              if (message.kind === "bot-typing") {
                return (
                  <div key={message.id} className={styles.botRow}>
                    <div className={styles.typingBubble}>
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                );
              }
              if (message.kind === "buttons") {
                return (
                  <div key={message.id} className={styles.buttonRow}>
                    {message.options.map((opt) => (
                      <button
                        key={opt.label}
                        className={styles.quickReplyBtn}
                        onClick={opt.onClick}
                      >
                        {opt.icon}
                        {opt.label}
                      </button>
                    ))}
                  </div>
                );
              }
              if (message.kind === "select") {
                return (
                  <div key={message.id} className={styles.selectRow}>
                    <select
                      className={styles.selectInput}
                      defaultValue=""
                      onChange={(e) => {
                        if (e.target.value) message.onSelect(e.target.value);
                      }}
                    >
                      <option value="" disabled>
                        {message.placeholder}
                      </option>
                      {message.options.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              }
              return null;
            })}
          </div>

          <div className={styles.inputRow}>
            <input
              type="text"
              className={styles.textInput}
              placeholder={t.inputPlaceholder}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSend();
              }}
            />
            <button
              className={styles.sendBtn}
              onClick={handleSend}
              disabled={!inputValue.trim()}
              aria-label="Send"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}

      {!isOpen && !hintDismissed && (
        <div className={styles.fabHint} dir={t.dir}>
          <button
            className={styles.fabHintClose}
            onClick={() => setHintDismissed(true)}
            aria-label={t.closeChat}
          >
            <X size={12} />
          </button>
          {t.fabHint}
        </div>
      )}

      {!isOpen && (
        <button
          className={styles.fab}
          onClick={() => {
            setIsOpen(true);
            setHintDismissed(true);
          }}
          aria-label={t.openChat}
        >
          <span className={styles.fabPulse} />
          <BotFaceIcon size={48} />
        </button>
      )}
    </div>
  );
}
